import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Sparkles, CheckCircle, XCircle, ChevronRight, ArrowRight, MessageCircle, Volume2, Trophy, BookOpen,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import MathContent from "../components/MathContent";

/**
 * Khan-style lesson player:
 *   1. AI explanation
 *   2. Key takeaways summary
 *   3. 3 (free) / 10 (premium) practice questions
 *   4. Archie explains wrong answers
 *   5. XP reward on complete
 */
const LessonPlayer = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { fetchUser } = useAuth();
  const { t } = useSettings();

  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState("explanation"); // explanation → summary → practice → complete
  const [questions, setQuestions] = useState([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [selected, setSelected] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [archieResponse, setArchieResponse] = useState("");
  const [archieLoading, setArchieLoading] = useState(false);
  const [stats, setStats] = useState({ correct: 0, total: 0 });
  const [finishing, setFinishing] = useState(false);
  const [xpEarned, setXpEarned] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const l = await api.getLesson(lessonId);
        if (cancelled) return;
        setLesson(l);
      } catch (e) {
        toast.error("Lesson not found");
        navigate("/lessons");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [lessonId, navigate]);

  const speak = (text) => {
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 0.95;
      window.speechSynthesis.speak(u);
    } else {
      toast.error("Text-to-speech not supported in this browser");
    }
  };

  const startPractice = async () => {
    setPhase("practice");
    try {
      const qs = await api.getQuestions(lesson.category, lesson.question_count || 3, false, false);
      setQuestions(qs);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.error === "daily_limit_reached") {
        toast.error(detail.message || "Daily question limit reached");
        navigate("/membership");
      } else {
        toast.error("Failed to load practice questions");
      }
    }
  };

  const currentQ = questions[questionIndex];

  const checkAnswer = async () => {
    if (selected === null || !currentQ) return;
    const correct = selected === currentQ.correct_answer;
    setShowResult(true);
    setStats((s) => ({ correct: s.correct + (correct ? 1 : 0), total: s.total + 1 }));

    // Submit answer for XP + mastery tracking
    try {
      await api.submitAnswer(
        currentQ.id, selected, currentQ.correct_answer,
        lesson.category, currentQ.topic, correct
      );
    } catch (e) { /* silent */ }

    if (!correct) {
      setArchieLoading(true);
      try {
        const r = await api.archieExplainWrong({
          question: currentQ.question,
          userAnswer: currentQ.options[selected],
          correctAnswer: currentQ.options[currentQ.correct_answer],
          topic: currentQ.topic,
          category: lesson.category,
        });
        setArchieResponse(r.response || "");
      } catch (e) {
        setArchieResponse("");
      } finally {
        setArchieLoading(false);
      }
    }
  };

  const nextQuestion = () => {
    if (questionIndex < questions.length - 1) {
      setQuestionIndex((i) => i + 1);
      setSelected(null);
      setShowResult(false);
      setArchieResponse("");
    } else {
      finishLesson();
    }
  };

  const finishLesson = async () => {
    setFinishing(true);
    try {
      const r = await api.completeLesson(lessonId);
      setXpEarned(r.xp_earned || 25);
      await fetchUser();
      setPhase("complete");
    } catch (e) {
      toast.error("Failed to complete lesson");
    } finally {
      setFinishing(false);
    }
  };

  if (loading || !lesson) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Sparkles className="w-12 h-12 text-indigo-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="lesson-player">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase font-bold text-indigo-500 tracking-wide">
            <BookOpen className="w-3 h-3 inline mr-1" />
            {lesson.category}
          </p>
          <h1 className="font-heading font-black text-2xl text-slate-800" data-testid="lesson-title">
            {lesson.title}
          </h1>
          <p className="text-sm text-slate-500">Topic: {lesson.topic}</p>
        </div>
        <button
          onClick={() => speak(lesson.explanation)}
          className="flex items-center gap-1 px-3 py-2 bg-sky-100 text-sky-700 rounded-full text-sm font-bold hover:bg-sky-200"
          data-testid="lesson-tts-btn"
        >
          <Volume2 className="w-4 h-4" />
          Read
        </button>
      </div>

      {/* Phase: Explanation */}
      {phase === "explanation" && (
        <div className="card-pop space-y-4" data-testid="lesson-phase-explanation">
          <div className="prose max-w-none">
            {lesson.explanation.split(/\n\n+/).map((p, i) => (
              <div key={i} className="text-slate-700 leading-relaxed mb-4">
                <MathContent text={p} />
              </div>
            ))}
          </div>
          {lesson.visual_hint && (
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
              <p className="text-xs font-bold text-slate-500 uppercase mb-1">Visualize it</p>
              <div className="text-sm text-slate-600"><MathContent text={lesson.visual_hint} /></div>
            </div>
          )}
          <button
            onClick={() => setPhase("summary")}
            className="w-full btn-primary flex items-center justify-center gap-2"
            data-testid="lesson-to-summary-btn"
          >
            Continue to Summary <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Phase: Summary */}
      {phase === "summary" && (
        <div className="card-pop space-y-4" data-testid="lesson-phase-summary">
          <h2 className="font-heading font-bold text-xl text-slate-800 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-yellow-500" />
            {t("lesson_summary")}
          </h2>
          <ul className="space-y-3">
            {(lesson.summary || []).map((item, i) => (
              <li key={i} className="flex items-start gap-3 p-3 bg-indigo-50 rounded-xl">
                <CheckCircle className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-0.5" />
                <p className="text-slate-700 font-medium"><MathContent text={item} /></p>
              </li>
            ))}
          </ul>
          <button
            onClick={startPractice}
            className="w-full btn-primary flex items-center justify-center gap-2"
            data-testid="lesson-to-practice-btn"
          >
            {t("lesson_continue")} <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Phase: Practice */}
      {phase === "practice" && (
        <div className="card-pop space-y-4" data-testid="lesson-phase-practice">
          <div className="flex items-center justify-between">
            <p className="text-sm font-bold text-slate-500">
              Question {questionIndex + 1} / {questions.length}
            </p>
            <p className="text-sm text-lime-600 font-bold">
              {stats.correct}/{stats.total} correct
            </p>
          </div>

          {!currentQ ? (
            <div className="text-center py-8">
              <Sparkles className="w-10 h-10 text-indigo-500 mx-auto animate-spin" />
              <p className="mt-2 text-slate-500">Loading questions...</p>
            </div>
          ) : (
            <>
              <h3 className="font-heading font-bold text-lg text-slate-800">
                <MathContent text={currentQ.question} />
              </h3>
              <div className="space-y-2">
                {currentQ.options.map((opt, i) => {
                  let cls = "border-2 border-slate-200 hover:border-indigo-300";
                  if (showResult) {
                    if (i === currentQ.correct_answer) cls = "border-2 border-lime-400 bg-lime-50";
                    else if (i === selected) cls = "border-2 border-red-400 bg-red-50";
                    else cls = "border-2 border-slate-200 opacity-50";
                  } else if (selected === i) {
                    cls = "border-2 border-indigo-400 bg-indigo-50";
                  }
                  return (
                    <button
                      key={i}
                      onClick={() => !showResult && setSelected(i)}
                      disabled={showResult}
                      data-testid={`practice-option-${i}`}
                      className={`w-full p-3 rounded-2xl text-left font-medium transition-all ${cls}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                          showResult && i === currentQ.correct_answer ? "bg-lime-500 text-white"
                          : showResult && i === selected ? "bg-red-500 text-white"
                          : selected === i ? "bg-indigo-500 text-white"
                          : "bg-slate-100 text-slate-600"
                        }`}>
                          {showResult && i === currentQ.correct_answer ? <CheckCircle className="w-4 h-4" />
                            : showResult && i === selected ? <XCircle className="w-4 h-4" />
                            : String.fromCharCode(65 + i)}
                        </div>
                        <span className="text-slate-700"><MathContent text={opt} /></span>
                      </div>
                    </button>
                  );
                })}
              </div>

              {showResult && (
                <div className={`p-4 rounded-xl ${selected === currentQ.correct_answer ? "bg-lime-50 border border-lime-200" : "bg-rose-50 border border-rose-200"}`}>
                  <p className="font-bold text-slate-800 mb-1">
                    {selected === currentQ.correct_answer ? "Correct! 🎉" : "Not quite"}
                  </p>
                  <p className="text-sm text-slate-600"><MathContent text={currentQ.explanation} /></p>
                  {selected !== currentQ.correct_answer && (
                    <div className="mt-3 p-3 bg-white rounded-xl border border-rose-100" data-testid="archie-explain-box">
                      <div className="flex items-center gap-2 mb-1">
                        <MessageCircle className="w-4 h-4 text-pink-500" />
                        <p className="text-xs font-bold text-pink-600 uppercase">Archie says</p>
                      </div>
                      {archieLoading ? (
                        <p className="text-sm text-slate-400 italic">Archie is thinking...</p>
                      ) : (
                        <p className="text-sm text-slate-700">
                          <MathContent text={archieResponse || "Review the explanation above and try similar questions."} />
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-3">
                {!showResult ? (
                  <button
                    onClick={checkAnswer}
                    disabled={selected === null}
                    className="flex-1 btn-primary disabled:opacity-50"
                    data-testid="practice-check-btn"
                  >
                    Check Answer
                  </button>
                ) : (
                  <button
                    onClick={nextQuestion}
                    disabled={finishing}
                    className="flex-1 btn-primary"
                    data-testid="practice-next-btn"
                  >
                    {questionIndex < questions.length - 1 ? "Next Question" : t("lesson_complete")}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Phase: Complete */}
      {phase === "complete" && (
        <div className="card-pop text-center space-y-4 animate-pop-in" data-testid="lesson-phase-complete">
          <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto">
            <Trophy className="w-10 h-10 text-yellow-500" />
          </div>
          <h2 className="font-heading font-black text-3xl text-slate-800">Lesson Complete!</h2>
          <p className="text-slate-500">You earned <span className="font-bold text-indigo-600">+{xpEarned} XP</span></p>
          <p className="text-slate-600">Score: {stats.correct}/{stats.total}</p>
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => navigate("/lessons")}
              className="flex-1 btn-secondary"
              data-testid="lesson-new-btn"
            >
              {t("lesson_new")}
            </button>
            <button
              onClick={() => navigate("/dashboard")}
              className="flex-1 btn-primary"
              data-testid="lesson-dashboard-btn"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LessonPlayer;
